import { PageLayout } from "@/components/page-layout/PageLayout";
import { useCurrencySelection } from "@/redux/profile/hooks/UseCurrencySelection";
import { SpacingValues } from "@/lib/utils/SpacingValues";
import { CustomerInfoPairBox } from "@/features/product-and-price/info-boxes/customer/CustomerInfoPairBox";
import {
  StatisticsByCustomerDetailsQuery,
  StatisticsByCustomerDetailsQueryVariables,
  StatisticsByCustomerThreeYearsStatisticsDetailsQuery,
  StatisticsByCustomerThreeYearsStatisticsDetailsQueryVariables,
} from "@/gql/graphql";
import { gql, useQuery } from "@apollo/client";
import { Row, Space, Spacing } from "@stenajs-webui/core";
import { LoadingPanel } from "@stenajs-webui/panels";
import * as React from "react";
import { Helmet } from "react-helmet";
import {
  customerStatisticsDetailsFragment,
  threeYearsStatisticsCustomerDetailsFragment,
} from "../fragments/customer-fragment";
import { StatisticsByCustomerDetailsHeader } from "./StatisticsByCustomerDetailsHeader";
import { ThreeYearsPricePerMeterTable } from "./ThreeYearsPricePerMeterTable";
import { ThreeYearsTargetActualTable } from "./ThreeYearsTargetActualTable";
import { ThreeYearsVolumeExpectedTable } from "./ThreeYearsVolumeExpectedTable";
import { SpaceValues } from "@/lib/utils/SpaceValues";

const customerQuery = gql`
  query StatisticsByCustomerDetails($customerId: ID!) {
    productPrice {
      customers {
        byId(id: $customerId) {
          ...CustomerStatisticsDetails
        }
      }
    }
  }
  ${customerStatisticsDetailsFragment}
`;

const threeYearsStatisticsQuery = gql`
  query StatisticsByCustomerThreeYearsStatisticsDetails(
    $customerId: ID!
    $year: Int!
    $currencyCode: String!
  ) {
    statistics {
      customerDetails {
        threeYearsTargetAndActualByCustNoAndYear(
          customerNumber: $customerId
          year: $year
        ) {
          ...ThreeYearsTargetActual
        }
        threeYearsPricePerMeterByCustNoYearAndCurrency(
          customerNumber: $customerId
          year: $year
          currencyCode: $currencyCode
        ) {
          ...ThreeYearsPricePerMeter
        }
        threeYearsVolumeAndExpectedByCustNoAndYear(
          customerNumber: $customerId
          year: $year
        ) {
          ...ThreeYearsVolumeExpected
        }
      }
    }
  }
  ${threeYearsStatisticsCustomerDetailsFragment}
`;

interface Props {
  customerId: string;
  year: number;
}

export const StatisticsByCustomerDetails: React.FC<Props> = ({
  customerId,
  year,
}) => {
  const { handleCurrencyChange, currency } = useCurrencySelection();
  const { data: customerData, loading: customerLoading } = useQuery<
    StatisticsByCustomerDetailsQuery,
    StatisticsByCustomerDetailsQueryVariables
  >(customerQuery, {
    variables: {
      customerId,
    },
  });

  const { data, loading } = useQuery<
    StatisticsByCustomerThreeYearsStatisticsDetailsQuery,
    StatisticsByCustomerThreeYearsStatisticsDetailsQueryVariables
  >(threeYearsStatisticsQuery, {
    variables: {
      customerId,
      year,
      currencyCode: currency,
    },
  });

  const customer = customerData?.productPrice.customers.byId;
  const threeYearsTargetAndActual =
    data?.statistics.customerDetails.threeYearsTargetAndActualByCustNoAndYear;
  const threeYearsPricePerMeter =
    data?.statistics.customerDetails
      .threeYearsPricePerMeterByCustNoYearAndCurrency;
  const threeYearsVolumeExpected =
    data?.statistics.customerDetails.threeYearsVolumeAndExpectedByCustNoAndYear;

  if (customerLoading) {
    return (
      <Spacing num={SpacingValues.TWENTYFOUR}>
        <LoadingPanel />
      </Spacing>
    );
  }

  if (!customer) {
    return null;
  }

  return (
    <>
      <Helmet title="Customer - Statistics - FPP" />
      <StatisticsByCustomerDetailsHeader
        customerId={customerId}
        currency={currency}
        customerName={customer?.name ?? ""}
        handleCurrencyChange={handleCurrencyChange}
      />
      <PageLayout>
        <>
          <CustomerInfoPairBox customer={customer} />
          <Row width={"calc(100% - 48px)"}>
            <ThreeYearsTargetActualTable
              year={year}
              loading={loading}
              threeYearsTargetAndActual={threeYearsTargetAndActual ?? []}
            />
            <Space num={SpaceValues.TWENTYFOUR} />
            <ThreeYearsVolumeExpectedTable
              year={year}
              loading={loading}
              threeYearsVolumeExpected={threeYearsVolumeExpected ?? []}
            />
          </Row>

          <ThreeYearsPricePerMeterTable
            year={year}
            currencyCode={currency}
            threeYearsPricePerMeter={threeYearsPricePerMeter ?? []}
            loading={loading}
            customerId={customerId}
          />
        </>
      </PageLayout>
    </>
  );
};
